import * as _ from 'lodash';
import { DateTime } from 'luxon';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { ReadingDataApi } from '../API/ReadingDataApi';
import { useAuth } from '../Auth/AuthProvider';
import { SessionEvent } from '../Session/ISessionEvent';
import {
  MAX_DELIVERY_ATTEMPTS,
  default as SessionDB,
  default as sessionDB,
} from '../Session/SessionDataStore';
import {
  SessionEndedEvent,
  SessionEndReason,
} from '../Session/SessionEvents/SessionEndedEvent';
import { SessionStartedEvent } from '../Session/SessionEvents/SessionStartedEvent';
import { uuidv4 } from '../Utils/General';
const IdleJS = require('idle-js');

const SESSION_STORAGE_KEY = 'session';
const SESSION_IDLE_TIMEOUT = 15 * 60 * 1000; // 15 mins
const SESSION_IDLE_DETECTION_FREQUENCY = 5 * 1000; // 5 sec
const SESSION_DATA_SYNC_FREQUENCY = 20 * 1000; // 20 sec
const SESSION_DATA_SYNC_BATCH_SIZE = 100;
const SESSION_ACTIVITY_PING_FREQUENCY = 10 * 1000; // 10 seconds
const SESSION_TIME_TO_IDLE_NO_INPUT = 5 * 60 * 1000; // 5 mins

interface SessionContextType {
  initSession: () => Promise<SessionInfo>;
  endSession: (
    session: SessionInfo,
    reason: SessionEndReason,
    endedAt?: number,
  ) => Promise<SessionInfo | undefined>;
  recordEvent: (event: SessionEvent, session?: SessionInfo) => Promise<string>;
  subscribeToActivityPings: (
    onPing: (activityInfo: ActivityPingData) => Promise<void>,
  ) => Function;
  flushActivityData: () => Promise<void>;
}

export interface ActivityPingData {
  timestamp: number;
  idleTime: number;
  activeTime: number;
}
export interface SessionInfo {
  sessionId: string;
  userId: string;
  createdAt: number;
  lastActiveAt: number;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

interface SessionProviderProps {
  children: ReactNode;
}

export const ReadingSessionProvider: React.FC<SessionProviderProps> = ({
  children,
}) => {
  const { user, userType } = useAuth();
  const location = useLocation();
  const [isIdle, setIsIdle] = useState(false);
  const [lastActiveStartedAt, setLastActiveStartedAt] = useState<number>(
    Date.now(),
  );
  const lastActiveStartedAtRef = useRef<number>();
  lastActiveStartedAtRef.current = lastActiveStartedAt;
  const [lastIdleStartedAt, setLastIdleStartedAt] = useState<number>();
  const lastIdleStartedAtRef = useRef<number>();
  lastIdleStartedAtRef.current = lastIdleStartedAt;

  const [activeTime, setActiveTime] = useState(0);
  const [idleTime, setIdleTime] = useState(0);
  const [lastActivityPingAt, setLastActivityPingAt] = useState(undefined);
  const [activityPingSubscriptions, setActivityPingSubscriptions] = useState<
    ((activityInfo: ActivityPingData) => Promise<void>)[]
  >([]);

  // This effect makes sure that the session is ended if it has been idle for more than 15 mins
  // It depends on userId because otherwise it uses a cached value, which is undefined initially
  useEffect(() => {
    getActiveSession(); // If the session has expired, send the event immediately once user enters the site. They might not stick around for 20 secs.

    const interval = setInterval(
      getActiveSession, // If too much time has passed since last activity, the active session will be ended
      SESSION_IDLE_DETECTION_FREQUENCY,
    );
    return () => clearInterval(interval);
  }, [user]);

  // This effect sends the pending data to the server every 10 seconds
  useEffect(() => {
    if (!user) return;

    sendPendingDataToServer();
    cleanupOldEvents();

    const interval = setInterval(
      sendPendingDataToServer,
      SESSION_DATA_SYNC_FREQUENCY,
    );
    return () => clearInterval(interval);
  }, [user, userType]);

  /**
   * Records a session event and stores it on the client, until it's synced to the server
   */
  async function recordEvent(
    event: SessionEvent,
    session?: SessionInfo,
  ): Promise<string> {
    if (!session) {
      session = await initSession();

      if (!session) {
        console.warn('Failed to init a session to record event', event);
        return;
      }

      await refreshSession();
    }

    const eventWithMetadata = {
      ...event,
      id: uuidv4(),
      recordedAt: Date.now(),
      sessionId: session.sessionId,
      userId: session.userId,
      metadata: event.metadata,
    };

    // We try to send the event immediately because we're more concerned about data accuracy than performance
    try {
      const usageApi = new ReadingDataApi();
      await usageApi.processReadingEvents([eventWithMetadata]);

      console.debug(
        `Reading event recorded: id ${eventWithMetadata.id} of type ${eventWithMetadata.type} for session ${session.sessionId}`,
      );
    } catch (ex) {
      console.warn('Failed to send event to server', eventWithMetadata, ex);

      // If the event fails to send, we store it in the local database and try to send it again later.

      try {
        await sessionDB.addEvent(eventWithMetadata);
      } catch (ex) {
        console.warn(
          'Failed to save event to local database. Skipping event.',
          eventWithMetadata,
          ex,
        );
      }
    }
  }

  /**
   * Returns the active session or starts a new one if there's none
   */
  async function initSession(): Promise<SessionInfo> {
    // If there is an active session, return it
    let sessionInfo = await getActiveSession();
    if (sessionInfo) {
      return sessionInfo;
    }

    // Otherwise, initialize a new session
    sessionInfo = {
      sessionId: uuidv4(),
      userId: user?.id,
      createdAt: Date.now(),
      lastActiveAt: Date.now(),
    };

    try {
      localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionInfo));
    } catch (ex) {
      console.warn('Error saving session to local storage', ex);
    }

    const event = new SessionStartedEvent();
    await recordEvent(event);

    return sessionInfo;
  }

  /**
   * Refreshes the active session by updating its lastActiveAt flag
   */
  async function refreshSession(timestamp?: number): Promise<void> {
    let sessionInfo = await getActiveSession();
    if (!sessionInfo) {
      console.warn('Failed to refresh session');
      return;
    }

    console.debug(
      `Refreshing session ${sessionInfo.sessionId} (previous activity: ${sessionInfo.lastActiveAt && DateTime.fromMillis(sessionInfo.lastActiveAt).diffNow('minutes')} minutes ago)`,
    );

    sessionInfo.lastActiveAt = timestamp ?? Date.now();

    try {
      localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionInfo));
    } catch (ex) {
      console.warn('Error saving session to local storage', ex);
    }
  }

  /**
   * Returns info about the active session
   */
  async function getActiveSession(): Promise<SessionInfo | undefined> {
    let sessionInfo;
    try {
      let sessionInfoJson = localStorage.getItem(SESSION_STORAGE_KEY);
      if (sessionInfoJson) {
        sessionInfo = JSON.parse(sessionInfoJson);

        // user is undefined while it's being initialized.
        // Only end session when we know the actual current user (or that there's none)
        if (sessionInfo.userId !== user?.id && user !== undefined) {
          await endSession(sessionInfo, SessionEndReason.SignOut);
          return undefined;
        }

        if (
          !sessionInfo.lastActiveAt ||
          sessionInfo.lastActiveAt < Date.now() - SESSION_IDLE_TIMEOUT
        ) {
          const expiredAt = sessionInfo.lastActiveAt
            ? sessionInfo.lastActiveAt + SESSION_IDLE_TIMEOUT
            : Date.now();
          console.debug(
            `Reading session has expired at ${new Date(expiredAt)}`,
          );

          await endSession(
            sessionInfo,
            SessionEndReason.SessionTimeout,
            expiredAt,
          );

          return undefined;
        }

        return sessionInfo;
      }
    } catch (ex) {
      console.warn('Error reading session from local storage', ex);
    }
  }

  /**
   * Ends the active session
   */
  async function endSession(
    session: SessionInfo,
    reason: SessionEndReason,
    endedAt?: number,
  ): Promise<SessionInfo | undefined> {
    const event = new SessionEndedEvent(reason);
    if (endedAt) {
      event.recordedAt = endedAt;
    }
    await recordEvent(event, session);

    try {
      localStorage.removeItem(SESSION_STORAGE_KEY);
    } catch (ex) {
      console.warn('Error removing session from local storage', ex);
    }

    return session;
  }

  /**
   * Sends the events that have not yet been synced to the server
   */
  async function sendPendingDataToServer(): Promise<void> {
    let eventsBatch;
    try {
      eventsBatch = await SessionDB.getEventsBatchPendingSync(
        SESSION_DATA_SYNC_BATCH_SIZE,
      );
    } catch (ex) {
      console.warn(
        'Failed to get pending events from local SB. Aborting sync.',
        ex,
      );
      return;
    }

    // Filter only the events that belong to the current user
    const userEventsBatch = eventsBatch.filter(
      (event) => user && event.userId === user.id,
    );

    console.debug(
      `Found ${userEventsBatch.length} reading session events to send`,
      {
        total: eventsBatch.length,
        byCurrentUser: userEventsBatch.length,
      },
    );

    if (!userEventsBatch.length) {
      console.debug('No pending reading events to send for the current user');
      return;
    }

    console.debug(`Found ${userEventsBatch.length} reading session events`, {
      total: eventsBatch.length,
      byCurrentUser: userEventsBatch.length,
    });

    const userEventsBatchBySessionId = _.groupBy(userEventsBatch, 'sessionId');

    for (const sessionId in userEventsBatchBySessionId) {
      const sessionEvents = userEventsBatchBySessionId[sessionId];

      console.debug(`Sending pending reading events to server`, {
        sessionId,
        count: sessionEvents.length,
        sessionEvents,
      });

      // Send to server
      try {
        const usageApi = new ReadingDataApi();
        await usageApi.processReadingEvents(sessionEvents);

        console.log('Events sent to server', sessionEvents.length);

        // Clear synced events
        try {
          await SessionDB.deleteSyncedEvents(sessionEvents);
        } catch (error) {
          console.warn('Failed to delete events', error, sessionEvents);
        }

        console.log('Events deleted from local db', sessionEvents.length);
      } catch (ex) {
        console.warn(
          'Failed to send reading events to server',
          ex,
          sessionEvents,
        );

        if (ex?.code === 'ERR_NETWORK') {
          console.log(
            'Not counting ERR_NETWORK failure when syncing reading events as a failed delivery attempt',
            ex,
            sessionEvents,
          );
          continue;
        } else {
          const deletedEvents =
            await SessionDB.processFailedEvents(sessionEvents);
          if (deletedEvents.length) {
            console.warn(
              `Deleted ${deletedEvents.length} reading events from local DB after ${MAX_DELIVERY_ATTEMPTS} failed deliveryattempts`,
              deletedEvents,
            );
          }
        }
      }
    }
  }

  async function cleanupOldEvents(): Promise<void> {
    const deletedEvents = await SessionDB.deleteOldEvents();
    if (deletedEvents.length) {
      console.warn('Cleaned up old undelivered reading session events', {
        total: deletedEvents.length,
        events: deletedEvents,
      });
    }
  }

  // LAMBDA-81109: We're intentionally memorizing the handlers and using `ref`s instead of dependencies
  // This is because these handlers are passed to a 3rd party library, that won't react to state updates
  // And destroying and recreating the 3rd party tracker to accomodate the pattern, causes side effects
  const onIdle = useCallback(() => {
    console.debug('Switching reading activity tracking to IDLE', {
      timestamp: new Date(),
    });
    // Calculate final active time before going idle
    setActiveTime(
      (prev) => prev + (Date.now() - lastActiveStartedAtRef.current) || 0,
    );
    setLastIdleStartedAt(Date.now());
    setIsIdle(true);
  }, []);

  const onActive = useCallback(() => {
    console.debug('Switching reading activity tracking to ACTIVE', {
      timestamp: new Date(),
    });
    // Calculate final idle time before going active
    setIdleTime(
      (prev) => prev + (Date.now() - lastIdleStartedAtRef.current) || 0,
    );
    setLastActiveStartedAt(Date.now());
    setIsIdle(false);
  }, []);

  // This effect starts idle tracking and pings activity every minute
  useEffect(() => {
    const idleTracker = new IdleJS({
      idle: SESSION_TIME_TO_IDLE_NO_INPUT,
      events: ['mousemove', 'keydown', 'mousedown', 'touchstart'],
      onIdle: onIdle,
      onHide: onIdle,
      onActive: onActive,
      onShow: onActive,
    });

    idleTracker.start();

    const interval = setInterval(() => {
      setLastActivityPingAt(Date.now());
    }, SESSION_ACTIVITY_PING_FREQUENCY);

    return () => {
      clearInterval(interval);
      idleTracker.stop();
    };
  }, [onIdle, onActive]);

  const flushActivityData = useCallback(async () => {
    const now = Date.now();

    // Calculate accumulated times since last active/idle state change
    const currentIdleTime = isIdle ? now - lastIdleStartedAt : 0;
    const currentActiveTime = !isIdle ? now - lastActiveStartedAt : 0;

    const idleTimeAccumulated = idleTime + currentIdleTime;
    const activeTimeAccumulated = activeTime + currentActiveTime;

    console.debug('Flushing reading activity data', {
      idleTime: idleTimeAccumulated,
      activeTime: activeTimeAccumulated,
      activityPingSubscriptions,
      timestamp: new Date(now),
    });

    // Report the activity to all subscribers
    for (const onPing of activityPingSubscriptions) {
      await onPing({
        timestamp: now,
        idleTime: idleTimeAccumulated,
        activeTime: activeTimeAccumulated,
      });
    }

    // Reset counters, as each flush is additive
    setIdleTime(0);
    setActiveTime(0);
    setLastIdleStartedAt(now);
    setLastActiveStartedAt(now);
  }, [
    idleTime,
    activeTime,
    isIdle,
    lastActiveStartedAt,
    lastIdleStartedAt,
    activityPingSubscriptions,
  ]);

  const flushActivityDataRef = useRef(flushActivityData);
  flushActivityDataRef.current = flushActivityData;

  // This effect reports activity updates to subscribes: on timer + when user navigates away from the page
  useEffect(() => {
    // Report activity every minute and on React navigation
    flushActivityDataRef.current();
  }, [location, lastActivityPingAt]);

  useEffect(() => {
    // Report activity before closing the tab/window
    window.addEventListener('beforeunload', flushActivityData);

    return () => {
      window.removeEventListener('beforeunload', flushActivityData);
    };
  }, [flushActivityData]);

  /**
   * Lets components subscribe to activity pings
   */
  function subscribeToActivityPings(
    onPing: (activityInfo: ActivityPingData) => Promise<void>,
  ): Function {
    setActivityPingSubscriptions((subs) => [...subs, onPing]);

    return () => {
      setActivityPingSubscriptions((subs) =>
        subs.filter((sub) => sub !== onPing),
      );
    };
  }

  return (
    <SessionContext.Provider
      value={{
        initSession,
        endSession,
        recordEvent,
        subscribeToActivityPings,
        flushActivityData,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = (): SessionContextType => {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a ReadingSessionProvider');
  }
  return context;
};
